export type AwsCliUA = {
  type: "aws-cli";
  raw: string;
  cliVersion: string;
  meta: Record<string, string | true>;
};

export type BrowserUA = {
  type: "browser";
  raw: string;
  browser: { name: string; version: string };
  os: { name: string; version?: string };
};

export type ProductUA = {
  type: "product-list";
  raw: string;
  products: { name: string; version?: string }[];
  comment?: string;
};

export type ParsedUA = AwsCliUA | BrowserUA | ProductUA;

export function parseUserAgent(raw: string): ParsedUA {
  const trimmed = raw.trim().replace(/^\[|\]$/g, "");

  /* AWS-CLI style ──────────────────────────────────────────── */
  if (trimmed.startsWith("aws-cli/")) {
    const meta: Record<string, string | true> = {};
    let cliVersion = "";

    trimmed.split(/\s+/).forEach((tok) => {
      const [, key, , val] = tok.match(/^([^/#]+)([/#]?)(.*)$/) ?? [];
      const value = val || true;

      if (key === "aws-cli") {
        cliVersion = value as string;
        return;
      }
      const cleaned = key.replace(/^md\//, "").replace(/^cfg\//, "");
      meta[cleaned] = value;
    });

    return { type: "aws-cli", raw, cliVersion, meta };
  }

  /* Browser style ──────────────────────────────────────────── */
  if (
    /\([^)]+\)/.test(trimmed) &&
    /(Mozilla|AppleWebKit|Chrome|Safari|Firefox|Edge)\//i.test(trimmed)
  ) {
    const osMatch = trimmed.match(/\(([^)]+)\)/);
    const osParts = osMatch ? osMatch[1].split(";").map((s) => s.trim()) : [];

    const nameVersionPairs = [...trimmed.matchAll(/([A-Za-z-]+)\/([\d.]+)/g)];
    const [browserName = "Unknown", browserVersion = ""] =
      nameVersionPairs.length > 0
        ? nameVersionPairs[nameVersionPairs.length - 1].slice(1)
        : [];

    return {
      type: "browser",
      raw,
      browser: { name: browserName, version: browserVersion },
      os: { name: osParts[0] ?? "Unknown", version: osParts[1] },
    };
  }

  /* Generic product list fallback ─────────────────────────── */
  const products: { name: string; version?: string }[] = [];
  let comment: string | undefined;

  // Pull off a trailing (…) comment if present
  const commentMatch = trimmed.match(/\s*\(([^)]+)\)\s*$/);
  let remainder = trimmed;
  if (commentMatch) {
    comment = commentMatch[1];
    remainder = trimmed.slice(0, commentMatch.index).trim();
  }

  remainder.split(/\s+/).forEach((tok) => {
    const [name, version] = tok.split("/");
    products.push({ name, version });
  });

  return { type: "product-list", raw, products, comment };
}

export const getUserIdentity = (userIdentity: any): string =>
  typeof userIdentity === "string"
    ? userIdentity
    : userIdentity?.principalId?.split(":")?.[1] ||
      userIdentity?.arn?.split("/")?.at(-1) ||
      "";
